import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import countries from '@/components/Countries';
import UserService from '@/api/users.service';
import { useToast } from '@/hooks/use-toast';

export default function Profile() {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState({
    publicId: '',
    username: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    gender: '',
    city: '',
    physicalAddress: '',
    country: '',
    avatar: '',
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      setIsLoading(true);
      try {
        const response = await UserService.getSelf();
        console.log("getSelf response.data", response.data);
        if (response.data.result === 'success') {
          setProfile(response.data.self);
        } else {
          showToastWithTimeout('error', 'Error', response.data.message, 3000);
        }
      } catch (error) {
        console.error('Failed to fetch user details', error);
        showToastWithTimeout('error', 'Error', 'Failed to fetch user details', 3000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  const handleSubmit = async () => {
    setIsEditing(false);
    try {
      const response = await UserService.updateSpecificUser(profile.publicId, profile);
      if (response.data.result === 'success') {
        showToastWithTimeout('success', '✅ Success', 'Profile updated successfully', 3000);
      } else {
        showToastWithTimeout('error', 'Error', response.data.message, 3000);
      }
    } catch (error) {
      console.error('Failed to update profile', error);
      showToastWithTimeout('error', 'Error', 'Failed to update profile', 3000);
    } finally {
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-12 bg-white p-5">
        <div className="border-b border-gray-900/10 pb-12">
          <h2 className="text-base/7 font-semibold text-gray-900">Profile</h2>
          <p className="mt-1 text-sm/6 text-gray-600">
            This information will be displayed publicly so be careful what you share.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label htmlFor="username" className="block text-sm/6 font-medium text-gray-900">
                Username
              </label>
              <div className="mt-2">
                <div className="flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600">
                  <div className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">app.moyoledgers.com/@</div>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    value={profile.username}
                    onChange={e => setProfile(prev => ({ ...prev, username: e.target.value }))}
                    className="block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6"
                  />
                </div>
              </div>
            </div>

            {/* Avatar 
            <div className="col-span-full">
              <label htmlFor="photo" className="block text-sm/6 font-medium text-gray-900">
                Photo
              </label>
              <div className="mt-2 flex items-center gap-x-3">
                {profile.avatar ? (
                  <img src={profile.avatar} alt="Profile" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <UserCircle className="size-12 text-gray-300" />
                )}
                <Button
                  type="button"
                  className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50"
                  onClick={() => document.getElementById('avatar-upload')?.click()}
                >
                  Change
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  id="avatar-upload"
                />
              </div>
            </div>
            */}

            <div className="sm:col-span-3">
              <label htmlFor="first-name" className="block text-sm/6 font-medium text-gray-900">
                First name
              </label>
              <div className="mt-2">
                <Input
                  id="first-name"
                  name="first-name"
                  type="text"
                  value={profile.firstName}
                  onChange={e => setProfile(prev => ({ ...prev, firstName: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="last-name" className="block text-sm/6 font-medium text-gray-900">
                Last name
              </label>
              <div className="mt-2">
                <Input
                  id="last-name"
                  name="last-name"
                  type="text"
                  value={profile.lastName}
                  onChange={e => setProfile(prev => ({ ...prev, lastName: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                Email address
              </label>
              <div className="mt-2">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={profile.emailAddress}
                  onChange={e => setProfile(prev => ({ ...prev, emailAddress: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="country" className="block text-sm/6 font-medium text-gray-900">
                Country
              </label>
              <div className="mt-2 grid grid-cols-1">
                <Select
                  name="country"
                  value={profile.country}
                  onValueChange={(value) => setProfile(prev => ({ ...prev, country: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="col-span-full">
              <label htmlFor="street-address" className="block text-sm/6 font-medium text-gray-900">
                Street address
              </label>
              <div className="mt-2">
                <Input
                  id="street-address"
                  name="street-address"
                  type="text"
                  value={profile.physicalAddress}
                  onChange={e => setProfile(prev => ({ ...prev, physicalAddress: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="sm:col-span-2 sm:col-start-1">
              <label htmlFor="city" className="block text-sm/6 font-medium text-gray-900">
                City
              </label>
              <div className="mt-2">
                <Input
                  id="city"
                  name="city"
                  type="text"
                  value={profile.city}
                  onChange={e => setProfile(prev => ({ ...prev, city: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="phone" className="block text-sm/6 font-medium text-gray-900">
                Phone
              </label>
              <div className="mt-2">
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={profile.phoneNumber}
                  onChange={e => setProfile(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <Button type="button" className="text-sm/6 font-semibold">
            Cancel
          </Button>
          <Button
            type="submit"
            className="rounded-md bg-[#0E415E] px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-[#1F628D] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0E415E]"
          >
            {isEditing ? <Loader2 className="animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </form>
  );
}